// frontend/src/components/Mobile/MobilePickup.jsx
// QR scanner + manual entry → confirmation step → pickup API

import React, { useState, useEffect, useRef, useCallback } from 'react';
import jsQR from 'jsqr';
import { fastReactAPI } from '../../../api/client';
import { FiCheckCircle, FiAlertCircle, FiCamera, FiX, FiPackage } from 'react-icons/fi';
import MobileLayout from '../MobileLayout';
import Toast    from '../../Common/Toast';
import { useToast } from '../../../hooks/useToast';
import '../Mobile.css';

const MobilePickup = () => {
  const [input,       setInput]       = useState('');
  const [loading,     setLoading]     = useState(false);
  const { toasts, removeToast, toast_success, toast_error, toast_warning } = useToast();
  const [result,      setResult]      = useState(null);
  const [cameraError, setCameraError] = useState('');  // camera-specific — shown inline
  const [scanning,    setScanning]    = useState(false);
  // Confirmation step: holds the code to be confirmed
  const [pendingCode,  setPendingCode]  = useState(null);
  const [cameraReady,  setCameraReady]  = useState(false); // frame data flowing

  const videoRef  = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef    = useRef(null);
  const inputRef  = useRef(null);

  // ── Scan loop (jsQR) ──────────────────────────────────────────────────────
  const scanLoop = useCallback(() => {
    const video  = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || !streamRef.current) return;

    if (video.readyState === video.HAVE_ENOUGH_DATA &&
        video.videoWidth > 0 && video.videoHeight > 0) {
      canvas.width  = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      ctx.drawImage(video, 0, 0);

      // Skip black frames
      const sample = ctx.getImageData(
        Math.floor(canvas.width / 2) - 10, Math.floor(canvas.height / 2) - 10,
        20, 20
      );
      const avg = sample.data.reduce((s, v, i) => i % 4 === 3 ? s : s + v, 0)
                  / (sample.data.length * 0.75);
      if (avg < 5) { rafRef.current = requestAnimationFrame(scanLoop); return; }
      // Mark camera as producing real frames
      setCameraReady(c => c || true);

      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const code = jsQR(imageData.data, imageData.width, imageData.height, {
        inversionAttempts: 'dontInvert',
      });
      if (code?.data) {
        const val = code.data.trim().toUpperCase();
        stopScan();
        setInput(val);
        setPendingCode(val);  // → show confirmation, don't call API yet
        return;
      }
    }
    rafRef.current = requestAnimationFrame(scanLoop);
  }, []); // eslint-disable-line

  // ── Camera start/stop ─────────────────────────────────────────────────────
  const startScan = async () => {
    setCameraError('');
    setCameraReady(false);
    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraError('Câmara não suportada. A app precisa de HTTPS.');
      return;
    }

    // Try rear camera; fall back to any camera if device doesn't support constraint
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' } },
      });
    } catch {
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: true });
      } catch (e2) {
        const msg = e2.name === 'NotAllowedError'
          ? 'Permissão de câmara negada. Verifique as definições do browser.'
          : e2.name === 'NotFoundError'
          ? 'Câmara não encontrada neste dispositivo.'
          : 'Erro ao aceder à câmara: ' + e2.message;
        setCameraError(msg);
        return;
      }
    }

    streamRef.current = stream;
    setScanning(true);
    // video is always in DOM — no setTimeout needed

    const video = videoRef.current;
    if (!video) { stopScan(); return; }

    // GPU compositing — prevents black video on many Android Chrome versions
    video.style.transform = 'translateZ(0)';
    video.style.webkitTransform = 'translateZ(0)';
    video.setAttribute('playsinline', 'true');
    video.setAttribute('webkit-playsinline', 'true');
    video.setAttribute('muted', 'true');
    video.muted     = true;
    video.srcObject = stream;

    // canplay fires when enough data exists to start; fallback after 1.5s
    await new Promise((resolve) => {
      const go = () => {
        video.removeEventListener('canplay', go);
        video.play().catch(() => {}).finally(resolve);
      };
      video.addEventListener('canplay', go);
      setTimeout(() => { video.removeEventListener('canplay', go); video.play().catch(() => {}).finally(resolve); }, 1500);
    });

    rafRef.current = requestAnimationFrame(scanLoop);
  };

  const stopScan = useCallback(() => {
    if (rafRef.current)    cancelAnimationFrame(rafRef.current);
    if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    rafRef.current = null; streamRef.current = null;
    setScanning(false);
    setCameraReady(false);
  }, []);

  useEffect(() => () => stopScan(), [stopScan]);

  // ── Manual form submit → confirmation step ────────────────────────────────
  const handleSubmit = (e) => {
    e.preventDefault();
    const code = input.trim().toUpperCase();
    if (!code) return;
    setPendingCode(code);
  };

  // ── Confirmed — call the API ──────────────────────────────────────────────
  const doPickup = async () => {
    if (!pendingCode) return;
    setLoading(true);
    setCameraError('');
    try {
      const r = await fastReactAPI.pickupContainer(pendingCode);
      setResult(r.data);
      setPendingCode(null);
      setInput('');
      if (r.data.already_completed)
        toast_warning(`Ordem ${r.data.po_setup?.po_code_line_full} já estava recolhida`);
      else
        toast_success(`Recolha de ${r.data.po_setup?.po_code_line_full} registada`);
    } catch (e) {
      toast_error(e.response?.data?.error || 'Erro ao processar recolha');
      setPendingCode(null);
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setResult(null);
    setInput(''); setPendingCode(null);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <MobileLayout title="Recolha de Carrinho" backTo="/mobile/plano-teares">
      <canvas ref={canvasRef} style={{ display: 'none' }} />

      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

        {/* Camera viewfinder — always in DOM to keep ref stable; shown via display */}
        <div style={{ display: scanning ? 'block' : 'none' }}>
          {/* No overflow:hidden or borderRadius — these can black-out video on Android */}
          <div style={{ position: 'relative', background: '#000',
                        width: '100%', paddingTop: '100%' }}>
            <video ref={videoRef} autoPlay playsInline muted
              style={{ position: 'absolute', top: 0, left: 0,
                       width: '100%', height: '100%',
                       objectFit: 'fill',        /* 'fill' is more reliable than 'cover' on Android */
                       display: 'block' }} />
            {/* Initialising overlay */}
            {scanning && !cameraReady && (
              <div style={{ position:'absolute', inset:0, display:'flex',
                            alignItems:'center', justifyContent:'center',
                            background:'rgba(0,0,0,0.6)' }}>
                <span style={{ color:'white', fontSize:13 }}>A iniciar câmara…</span>
              </div>
            )}
            <div style={{ position: 'absolute', inset: 0, display: 'flex',
                          alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
              <div style={{ width: '58%', aspectRatio: '1', position: 'relative' }}>
                {[['top','left'],['top','right'],['bottom','left'],['bottom','right']].map(([v,h]) => (
                  <div key={v+h} style={{ position:'absolute',[v]:0,[h]:0, width:20, height:20,
                    borderTop:    v==='top'    ? '3px solid var(--primary)' : 'none',
                    borderBottom: v==='bottom' ? '3px solid var(--primary)' : 'none',
                    borderLeft:   h==='left'   ? '3px solid var(--primary)' : 'none',
                    borderRight:  h==='right'  ? '3px solid var(--primary)' : 'none',
                  }} />
                ))}
              </div>
            </div>
            <button type="button" onClick={stopScan}
              style={{ position:'absolute', top:10, right:10, display:'flex', alignItems:'center',
                       gap:5, background:'rgba(0,0,0,0.65)', border:'none', color:'white',
                       borderRadius:8, padding:'6px 12px', cursor:'pointer', fontSize:13 }}>
              <FiX size={14} /> Cancelar
            </button>
            <div style={{ position:'absolute', bottom:14, left:0, right:0, textAlign:'center',
                          color:'white', fontSize:12, textShadow:'0 1px 4px rgba(0,0,0,0.8)' }}>
              Aponte para o QR Code da etiqueta
            </div>
          </div>
        </div>

        {/* ── Confirmation step ──────────────────────────────────────────── */}
        {pendingCode && !loading && (
          <div style={{ background:'white', border:'1px solid var(--gray-200)',
                        borderRadius:12, padding:18 }}>
            <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:14 }}>
              <FiPackage size={22} style={{ color:'var(--primary)', flexShrink:0 }} />
              <div>
                <div style={{ fontWeight:700, fontSize:14 }}>Confirmar Recolha</div>
                <div style={{ fontSize:12, color:'var(--gray-500)', marginTop:2 }}>
                  Verifique o código antes de confirmar.
                </div>
              </div>
            </div>

            <div style={{ background:'var(--gray-50)', borderRadius:8, padding:'10px 14px',
                          marginBottom:16, fontFamily:'monospace', fontWeight:700,
                          fontSize:16, color:'var(--gray-900)', letterSpacing:'0.5px',
                          wordBreak:'break-all' }}>
              {pendingCode}
            </div>

            <div style={{ display:'flex', gap:8 }}>
              <button type="button"
                onClick={() => { setPendingCode(null); setInput(''); }}
                style={{ flex:1, height:46, border:'1px solid var(--gray-300)',
                         borderRadius:10, background:'white', fontSize:14,
                         fontWeight:600, color:'var(--gray-600)', cursor:'pointer' }}>
                Cancelar
              </button>
              <button type="button" onClick={doPickup}
                style={{ flex:2, height:46, background:'var(--primary)', color:'white',
                         border:'none', borderRadius:10, fontSize:14,
                         fontWeight:700, cursor:'pointer' }}>
                Confirmar Recolha ✓
              </button>
            </div>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div style={{ display:'flex', alignItems:'center', gap:10, padding:16,
                        background:'white', borderRadius:12,
                        border:'1px solid var(--gray-200)' }}>
            <div className="mb-spinner" style={{ flexShrink:0 }} />
            <span style={{ fontSize:14, color:'var(--gray-600)' }}>A processar recolha…</span>
          </div>
        )}

        {/* ── Result ────────────────────────────────────────────────────── */}
        {result?.success && (
          <div style={{
            background: result.already_completed ? '#fef3c7' : '#dcfce7',
            border: `1px solid ${result.already_completed ? '#fcd34d' : '#86efac'}`,
            borderRadius:12, padding:16,
          }}>
            <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:10 }}>
              <FiCheckCircle size={22} style={{
                color: result.already_completed ? '#92400e' : '#166534', flexShrink:0,
              }} />
              <span style={{ fontWeight:700, fontSize:15,
                             color: result.already_completed ? '#92400e' : '#166534' }}>
                {result.already_completed
                  ? 'Esta ordem já estava recolhida'
                  : 'Recolha registada com sucesso!'}
              </span>
            </div>
            <div style={{ fontSize:13,
                          color: result.already_completed ? '#92400e' : '#166534',
                          lineHeight:1.7 }}>
              <div style={{ fontFamily:'monospace', fontWeight:700, fontSize:15 }}>
                {result.po_setup?.po_code_line_full}
              </div>
              {result.po_setup?.machine && (
                <div>Máquina: <strong>{result.po_setup.machine}</strong></div>
              )}
              {result.po_setup?.container_material && (
                <div>🛒 <strong>{result.po_setup.container_material}</strong></div>
              )}
              {result.po_setup?.product_name && (
                <div style={{ marginTop:4 }}>{result.po_setup.product_name}</div>
              )}
              <div style={{ marginTop:6, fontSize:12, opacity:0.75 }}>
                {result.prepared_count}/{result.total_count} itens concluídos
              </div>
            </div>
            <button type="button" className="mb-btn-primary"
              style={{ marginTop:14,
                       background: result.already_completed ? '#92400e' : '#166534' }}
              onClick={reset}>
              Nova Recolha
            </button>
          </div>
        )}

        {/* Camera error — shown inline near the button */}
        {cameraError && (
          <div style={{ background:'#fee2e2', border:'1px solid #fca5a5',
                        borderRadius:12, padding:14,
                        display:'flex', alignItems:'flex-start', gap:10 }}>
            <FiAlertCircle size={18} style={{ color:'var(--error)', flexShrink:0, marginTop:2 }} />
            <div>
              <div style={{ fontWeight:600, color:'var(--error)', fontSize:13 }}>{cameraError}</div>
              <button type="button" onClick={() => setCameraError('')}
                style={{ marginTop:6, background:'none', border:'none', color:'var(--error)',
                         fontSize:12, cursor:'pointer', padding:0, textDecoration:'underline' }}>
                Fechar
              </button>
            </div>
          </div>
        )}

        {/* ── Input form — shown when no pending/result ─────────────────── */}
        {!pendingCode && !result?.success && !loading && (
          <div className="mb-detail-header" style={{ marginBottom:0 }}>
            <div style={{ fontWeight:700, fontSize:14, marginBottom:4 }}>
              Leitura do QR Code
            </div>
            <div style={{ fontSize:12, color:'var(--gray-500)', marginBottom:12 }}>
              Leia o QR code da etiqueta ou introduza manualmente o código da ordem.
            </div>

            <form onSubmit={handleSubmit}>
              <input
                ref={inputRef}
                type="text"
                autoComplete="off"
                autoCapitalize="characters"
                style={{ width:'100%', height:48, border:'1px solid var(--gray-300)',
                         borderRadius:10, padding:'0 14px', fontSize:15,
                         fontFamily:'monospace', fontWeight:700,
                         outline:'none', boxSizing:'border-box', marginBottom:10 }}
                value={input}
                onChange={e => setInput(e.target.value.toUpperCase())}
                disabled={scanning}
                autoFocus
              />
              <div style={{ display:'flex', gap:8 }}>
                <button type="submit" className="mb-btn-primary"
                  style={{ flex:1, marginTop:0 }}
                  disabled={!input.trim()}>
                  Continuar
                </button>
                <button type="button" onClick={scanning ? stopScan : startScan}
                  style={{ width:50, height:50, flexShrink:0,
                           border:'1px solid var(--gray-300)', borderRadius:10,
                           background: scanning ? '#fee2e2' : 'white',
                           cursor:'pointer', display:'flex', alignItems:'center',
                           justifyContent:'center',
                           color: scanning ? 'var(--error)' : 'var(--primary)' }}>
                  {scanning ? <FiX size={20} /> : <FiCamera size={20} />}
                </button>
              </div>
            </form>
          </div>
        )}

        {!pendingCode && !result?.success && !scanning && !loading && (
          <div style={{ fontSize:11, color:'var(--gray-400)', textAlign:'center',
                        lineHeight:1.6 }}>
            Esta ação marca todos os materiais desta ordem como{' '}
            <strong>RECOLHIDOS</strong>.
          </div>
        )}
      </div>
      {/* Toast notifications */}
      <div className="toast-container">
        {toasts.map(toast => (
          <Toast key={toast.id} id={toast.id} message={toast.message}
            type={toast.type} duration={toast.duration}
            requireConfirm={toast.requireConfirm} onConfirm={toast.onConfirm}
            onClose={removeToast} />
        ))}
      </div>
    </MobileLayout>
  );
};

export default MobilePickup;